import React, { useEffect, useRef, useState } from "react";
import { Monitor, Tablet, Smartphone, ExternalLink, ArrowUpRight, X, Maximize2, Minimize2, RotateCw, Globe } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export function portfolioUrl(p) {
  const raw = p.website_url || (/ximmo24/i.test(p.title || "") ? "https://ximmo24.de" : /cleanora/i.test(p.title || "") ? "https://cleanora-reinigung.de" : "");
  try {
    const url = new URL(raw);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : "";
  } catch { return ""; }
}

export function PortfolioPreview({ p, onClose }) {
  const { lang } = useI18n();
  const de = lang === "de";
  const url = portfolioUrl(p);
  const dialog = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [expanded, setExpanded] = useState(false);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const modal = dialog.current;
    const overflow = document.body.style.overflow;
    modal.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      modal.close();
      document.body.style.overflow = overflow;
    };
  }, []);
  if (!url) return null;
  const devices = [
    ["desktop", Monitor, de ? "Computer" : "Desktop"],
    ["tablet", Tablet, "Tablet"],
    ["mobile", Smartphone, de ? "Mobil" : "Mobile"],
  ];
  return (
    <dialog ref={dialog} onCancel={onClose} aria-labelledby="portfolio-preview-title"
      className={`m-auto w-[calc(100%_-_1rem)] max-w-[1440px] border border-border rounded-2xl bg-background text-foreground p-0 backdrop:bg-black/80 backdrop:backdrop-blur-sm ${expanded ? "h-[96dvh]" : "h-[90dvh]"}`}
      data-testid="portfolio-preview">
      <div className="flex h-full min-h-0 flex-col">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border p-3 sm:p-4">
          <div className="min-w-0">
            <p className="text-xs text-primary font-semibold">LIVE EXPERIENCE</p>
            <h2 id="portfolio-preview-title" className="font-display font-bold truncate">{p.title}</h2>
          </div>
          <div className="flex shrink-0 gap-1">
            <button type="button" onClick={() => setExpanded(!expanded)} aria-label={de ? "Größe ändern" : "Resize"} className="p-2 rounded-lg hover:bg-secondary">{expanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}</button>
            <button type="button" onClick={onClose} aria-label={de ? "Schließen" : "Close"} className="p-2 rounded-lg hover:bg-secondary" autoFocus><X size={20} /></button>
          </div>
        </header>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-2">
          <div className="flex gap-1 rounded-xl bg-secondary p-1">
            {devices.map(([key, Icon, label]) => (
              <button key={key} type="button" onClick={() => setDevice(key)} aria-label={label} aria-pressed={device === key}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${device === key ? "bg-background text-primary shadow-sm" : "text-muted-foreground"}`}>
                <Icon size={16} /><span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <span className="hidden lg:inline text-xs text-muted-foreground truncate">{new URL(url).hostname}</span>
            <button type="button" onClick={() => setVersion(v => v + 1)} aria-label={de ? "Neu laden" : "Reload"} className="p-2 rounded-lg hover:bg-secondary"><RotateCw size={16} /></button>
            <a href={url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-secondary" aria-label={de ? "Website öffnen" : "Open website"}><ExternalLink size={16} /></a>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto bg-secondary/60 p-2 sm:p-4">
          <div className="h-full min-h-[200px] mx-auto overflow-hidden rounded-xl bg-white shadow-xl transition-[width] duration-300"
            style={{ width: device === "mobile" ? "390px" : device === "tablet" ? "768px" : "100%", maxWidth: "100%" }}>
            <iframe key={version} src={url} title={`${p.title} – Live preview`} className="h-full w-full border-0"
              referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              data-testid="portfolio-live-frame" />
          </div>
        </div>
        <footer className="shrink-0 border-t border-border px-3 py-2 text-xs text-muted-foreground">
          {de ? "Vorschau leer? Die Website kann die Einbettung einschränken." : "Preview blank? The website may restrict embedding."}
          {" "}<a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{de ? "Website öffnen" : "Open website"}</a>
        </footer>
      </div>
    </dialog>
  );
}

export function PortfolioCard({ p, index }) {
  const { lang, pickL } = useI18n();
  const de = lang === "de";
  const [open, setOpen] = useState(false);
  const url = portfolioUrl(p);
  return (
    <>
      <article className="group surface h-full overflow-hidden flex flex-col" data-testid={`portfolio-card-${index}`}>
        <div className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/15 via-background to-secondary p-6 sm:p-8">
          <div className="flex gap-1.5 mb-8" aria-hidden="true"><span className="h-2 w-2 rounded-full bg-primary" /><span className="h-2 w-2 rounded-full bg-primary/40" /><span className="h-2 w-2 rounded-full bg-primary/20" /></div>
          <Globe className="text-primary mb-4" size={36} />
          <h3 className="font-display text-3xl font-black break-words">{p.title}</h3>
          {url && <p className="mt-2 text-xs text-muted-foreground break-all">{new URL(url).hostname}</p>}
          {url && <button type="button" onClick={() => setOpen(true)} className="mt-6 btn-primary !px-4 !py-2.5 text-sm" data-testid={`portfolio-preview-${index}`}>{de ? "Website live erleben" : "Explore live website"}<ArrowUpRight size={18} /></button>}
        </div>
        <div className="p-6 flex flex-col flex-1">
          <span className="text-xs uppercase tracking-wide text-primary font-semibold">{p.category}</span>
          {pickL(p, "short") && <p className="mt-3 text-sm text-muted-foreground flex-1">{pickL(p, "short")}</p>}
          <div className="mt-4 flex flex-wrap gap-1.5">{(p.technologies || []).map(tec => <span key={tec} className="rounded-full bg-secondary px-2.5 py-1 text-xs">{tec}</span>)}</div>
          {!url && <p className="mt-4 text-xs text-muted-foreground">{de ? "Live-Vorschau folgt." : "Live preview coming soon."}</p>}
        </div>
      </article>
      {open && <PortfolioPreview p={p} onClose={() => setOpen(false)} />}
    </>
  );
}
